/**
 * Refine resource declarations.
 *
 * `name` matches the key in `providers/access-control.ts` AND the table (or
 * view) the Supabase data provider will query, so the sidebar, the access
 * matrix and the queries cannot drift apart. Where the two differ — a screen
 * that reads a view but is authorised as its base table — `meta.dataProviderName`
 * is not enough and the page passes an explicit `resource` instead; those are
 * called out in the page files.
 */
import type { ResourceProps } from '@refinedev/core'
import {
  AuditOutlined,
  BellOutlined,
  BookOutlined,
  CreditCardOutlined,
  CustomerServiceOutlined,
  DatabaseOutlined,
  DashboardOutlined,
  FileTextOutlined,
  FolderOpenOutlined,
  GlobalOutlined,
  SafetyCertificateOutlined,
  SettingOutlined,
  TeamOutlined,
  UserSwitchOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import { createElement } from 'react'

export const resources: ResourceProps[] = [
  {
    name: 'dashboard',
    list: '/',
    meta: { label: 'Tableau de bord', icon: createElement(DashboardOutlined) },
  },

  // ── Contenu ──────────────────────────────────────────────────────────────
  {
    name: 'subjects',
    list: '/subjects',
    create: '/subjects/create',
    edit: '/subjects/edit/:id',
    meta: { label: 'Matières', canDelete: true, icon: createElement(BookOutlined) },
  },
  {
    name: 'content_items',
    list: '/content-items',
    create: '/content-items/create',
    edit: '/content-items/edit/:id',
    meta: { label: 'Contenus', canDelete: true, icon: createElement(VideoCameraOutlined) },
  },
  {
    name: 'ingest',
    list: '/ingest',
    meta: { label: 'Ingestion', icon: createElement(DatabaseOutlined) },
  },

  // ── Utilisateurs ─────────────────────────────────────────────────────────
  {
    name: 'users',
    list: '/users',
    show: '/users/:id',
    meta: { label: 'Utilisateurs', icon: createElement(TeamOutlined) },
  },

  // ── Communauté (Annexe 1) ────────────────────────────────────────────────
  {
    name: 'reports',
    list: '/moderation',
    show: '/moderation/:id',
    meta: { label: 'Modération', icon: createElement(SafetyCertificateOutlined) },
  },
  {
    name: 'posts',
    list: '/community',
    meta: { label: 'Publications', icon: createElement(GlobalOutlined) },
  },

  // ── Abonnements ──────────────────────────────────────────────────────────
  {
    name: 'plans',
    list: '/plans',
    create: '/plans/create',
    edit: '/plans/edit/:id',
    meta: { label: 'Plans', canDelete: true, icon: createElement(CreditCardOutlined) },
  },
  {
    name: 'transactions',
    list: '/transactions',
    show: '/transactions/show/:id',
    meta: { label: 'Transactions', icon: createElement(FileTextOutlined) },
  },

  // ── Communication ────────────────────────────────────────────────────────
  {
    name: 'notifications',
    list: '/notifications/history',
    create: '/notifications/compose',
    meta: { label: 'Notifications', icon: createElement(BellOutlined) },
  },
  {
    name: 'support_messages',
    list: '/support',
    meta: { label: 'Support', icon: createElement(CustomerServiceOutlined) },
  },

  // ── CMS ──────────────────────────────────────────────────────────────────
  {
    name: 'cms_pages',
    list: '/cms/pages',
    meta: { label: 'Pages', icon: createElement(FolderOpenOutlined) },
  },
  {
    name: 'faq_items',
    list: '/cms/faq',
    meta: { label: 'FAQ', canDelete: true, icon: createElement(FolderOpenOutlined) },
  },

  // ── Système ──────────────────────────────────────────────────────────────
  {
    name: 'streams',
    list: '/reference-data',
    meta: { label: 'Filières & villes', icon: createElement(SettingOutlined) },
  },
  {
    name: 'programs',
    // Same screen, its own tab; hidden from the menu so the entry is not doubled.
    list: '/reference-data',
    meta: { label: 'Programmes', hide: true },
  },
  {
    name: 'cities',
    // Same screen, two tabs; hidden from the menu so the entry is not doubled.
    list: '/reference-data',
    meta: { label: 'Villes', hide: true },
  },
  {
    name: 'admin_users',
    list: '/admin-users',
    meta: { label: 'Administrateurs', icon: createElement(UserSwitchOutlined) },
  },
  {
    name: 'audit_log',
    list: '/audit-log',
    meta: { label: 'Journal d’audit', icon: createElement(AuditOutlined) },
  },
]
